import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, Plus, LayoutGrid, Inbox, Rocket, Pencil, Trash2, Hash, Check, X, CheckCircle2, XCircle, Clock } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { PageHead, PremiumBanner, StatCard } from "@/components/kit";
import ApplicationPanelModal from "@/components/ApplicationPanelModal";

const BTN_HEX = { blurple: "#5865F2", green: "#248046", red: "#da373c", grey: "#4e5058" };
const STATUS = { pending: { c: "#facc15", i: Clock }, accepted: { c: "#10b981", i: CheckCircle2 }, denied: { c: "#ef4444", i: XCircle } };

export default function Applications() {
  const { guildId, guild } = useGuild();
  const locked = false;
  const [tab, setTab] = useState("panels");
  const [panels, setPanels] = useState([]);
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadPanels = () => api.get(`/guilds/${guildId}/applications/panels`).then((r) => setPanels(r.data));
  const loadApps = () => api.get(`/guilds/${guildId}/applications${filter ? `?status=${filter}` : ""}`).then((r) => setApps(r.data));
  const loadStats = () => api.get(`/guilds/${guildId}/applications/stats`).then((r) => setStats(r.data));
  useEffect(() => { loadPanels(); loadApps(); loadStats(); }, [guildId, filter]);

  const savePanel = async (payload) => {
    setSaving(true);
    try {
      if (editing) await api.put(`/guilds/${guildId}/applications/panels/${editing.id}`, payload);
      else await api.post(`/guilds/${guildId}/applications/panels`, payload);
      toast.success(editing ? "Panel updated" : "Panel created");
      setModalOpen(false); setEditing(null); loadPanels();
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };
  const deletePanel = async (id) => {
    try { await api.delete(`/guilds/${guildId}/applications/panels/${id}`); toast.success("Panel deleted"); loadPanels(); }
    catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };
  const postPanel = async (id) => {
    try { const { data } = await api.post(`/guilds/${guildId}/applications/panels/${id}/post`); toast.success(`Posted to #${data.channel}`); loadPanels(); }
    catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };
  const simulate = async () => {
    try { await api.post(`/guilds/${guildId}/applications/simulate`); toast.success("Sample application submitted"); loadApps(); loadStats(); }
    catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };
  const review = async (id, action) => {
    try {
      const { data } = await api.post(`/guilds/${guildId}/applications/${id}/${action}`, { reason: action === "accept" ? "Approved" : "Not a fit" });
      toast.success(`Application ${data.status}`);
      setActive(data); loadApps(); loadStats();
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail || e.message)); }
  };

  return (
    <div>
      <PageHead title="Applications" desc="Staff & community application forms with review workflow and role automation.">
        <div className="flex gap-2">
          <button data-testid="app-simulate" onClick={simulate} className="border border-zinc-700 text-zinc-300 rounded-md px-4 py-2 hover:bg-zinc-900 flex items-center gap-2 text-sm"><Plus className="w-4 h-4" /> Simulate</button>
          <button data-testid="app-create-panel-btn" onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-volt text-volt-ink font-semibold rounded-md px-5 py-2 hover:bg-volt-hover flex items-center gap-2"><Plus className="w-4 h-4" /> Create Panel</button>
        </div>
      </PageHead>
      <PremiumBanner guildId={guildId} tier="Pro" locked={locked} />

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard icon={ClipboardList} label="Total" value={stats.total} />
          <StatCard icon={Clock} label="Pending" value={stats.pending} accent="#facc15" />
          <StatCard icon={CheckCircle2} label="Accepted" value={stats.accepted} accent="#10b981" />
          <StatCard icon={XCircle} label="Denied" value={stats.denied} accent="#ef4444" />
        </div>
      )}

      <div className="inline-flex bg-zinc-900 p-1 rounded-lg mb-6">
        <button data-testid="app-tab-panels" onClick={() => setTab("panels")} className={`px-4 py-1.5 rounded-md text-sm font-medium ${tab === "panels" ? "bg-zinc-800 text-white" : "text-zinc-400"}`}><LayoutGrid className="w-4 h-4 inline mr-1" /> Panels</button>
        <button data-testid="app-tab-submissions" onClick={() => setTab("submissions")} className={`px-4 py-1.5 rounded-md text-sm font-medium ${tab === "submissions" ? "bg-zinc-800 text-white" : "text-zinc-400"}`}><Inbox className="w-4 h-4 inline mr-1" /> Submissions</button>
      </div>

      {tab === "panels" && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {panels.map((p) => (
            <div key={p.id} data-testid="app-panel-card" className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-volt/15 flex items-center justify-center shrink-0"><ClipboardList className="w-5 h-5 text-volt" /></div>
                  <div className="min-w-0"><div className="font-head font-semibold truncate">{p.name}</div>
                    <div className="text-xs text-zinc-500 flex items-center gap-1"><Hash className="w-3 h-3" />{p.apply_channel || "no channel set"}</div></div>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0 ${p.enabled ? "bg-volt/15 text-volt" : "bg-zinc-800 text-zinc-500"}`}>{p.enabled ? "Enabled" : "Off"}</span>
              </div>
              <div className="text-xs text-zinc-500 mt-3 flex flex-wrap gap-x-4 gap-y-1">
                <span>{(p.questions || []).length} question{(p.questions || []).length !== 1 && "s"}</span>
                <span>{(p.manager_roles || []).length} manager role{(p.manager_roles || []).length !== 1 && "s"}</span>
                {p.posted_message_id && <span className="text-volt">● Posted</span>}
              </div>
              <div className="mt-3"><button className="text-white text-xs font-medium px-3 py-1.5 rounded" style={{ background: BTN_HEX[p.button_color] || BTN_HEX.blurple }}>{p.button_label}</button></div>
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-zinc-800">
                <button data-testid="app-panel-post" onClick={() => postPanel(p.id)} className="flex-1 text-xs bg-volt/10 text-volt border border-volt/30 rounded-md px-3 py-1.5 hover:bg-volt/20 flex items-center justify-center gap-1"><Rocket className="w-3.5 h-3.5" /> Post</button>
                <button data-testid="app-panel-edit" onClick={() => { setEditing(p); setModalOpen(true); }} className="text-xs bg-zinc-800 hover:bg-zinc-700 rounded-md px-3 py-1.5 flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                <button data-testid="app-panel-delete" onClick={() => deletePanel(p.id)} className="text-xs bg-zinc-800 hover:bg-red-500/20 hover:text-red-400 rounded-md px-2.5 py-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
          <button data-testid="app-create-panel-card" onClick={() => { setEditing(null); setModalOpen(true); }} className="border border-dashed border-zinc-700 rounded-xl p-5 flex flex-col items-center justify-center gap-2 text-zinc-500 hover:border-volt hover:text-volt min-h-[180px]"><Plus className="w-7 h-7" /> <span className="text-sm font-medium">Create Application Panel</span></button>
        </div>
      )}

      {tab === "submissions" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex gap-1 mb-3">
              {["", "pending", "accepted", "denied"].map((ff) => (
                <button key={ff} onClick={() => setFilter(ff)} className={`text-xs px-3 py-1 rounded-full ${filter === ff ? "bg-volt text-volt-ink" : "bg-zinc-800 text-zinc-400"}`}>{ff === "" ? "All" : ff}</button>
              ))}
            </div>
            <div className="space-y-2 max-h-[520px] overflow-y-auto">
              {apps.map((a) => { const S = STATUS[a.status] || STATUS.pending; return (
                <button key={a.id} data-testid="app-row" onClick={() => setActive(a)} className={`w-full text-left bg-black/30 border rounded-lg p-3 hover:border-zinc-600 transition-colors ${active?.id === a.id ? "border-volt/50" : "border-zinc-800"}`}>
                  <div className="flex items-center gap-2">
                    <img src={a.applicant_avatar} className="w-6 h-6 rounded-full bg-zinc-800" alt="" />
                    <span className="text-sm font-medium truncate flex-1">{a.applicant}</span>
                    <S.i className="w-4 h-4" style={{ color: S.c }} />
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">{a.panel_name}</div>
                </button>
              ); })}
              {apps.length === 0 && <p className="text-zinc-600 text-sm">No applications yet. Use "Simulate" to add one.</p>}
            </div>
          </div>

          <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl min-h-[540px]">
            {!active ? <div className="h-full flex items-center justify-center text-zinc-600 text-sm p-8">Select an application to review.</div> : (
              <div className="flex flex-col h-full">
                <div className="p-4 border-b border-zinc-800 flex items-center gap-3">
                  <img src={active.applicant_avatar} className="w-10 h-10 rounded-full bg-zinc-800" alt="" />
                  <div className="flex-1"><div className="font-head font-semibold">{active.applicant}</div><div className="text-xs text-zinc-500">{active.panel_name}</div></div>
                  <span className="text-xs px-2 py-1 rounded-full capitalize" style={{ background: `${(STATUS[active.status] || STATUS.pending).c}22`, color: (STATUS[active.status] || STATUS.pending).c }}>{active.status}</span>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {(active.answers || []).map((a, i) => (
                    <div key={i} data-testid="app-answer"><div className="text-xs text-zinc-500 uppercase tracking-wider">{a.question}</div><div className="text-sm text-zinc-200 mt-1 bg-black/30 rounded-md p-3">{a.answer}</div></div>
                  ))}
                  {active.reviewed_by && <div className="text-xs text-zinc-500">Reviewed by {active.reviewed_by} · {active.review_reason}</div>}
                </div>
                {active.status === "pending" && (
                  <div className="p-3 border-t border-zinc-800 flex gap-2">
                    <button data-testid="app-accept" onClick={() => review(active.id, "accept")} className="flex-1 bg-[#10b981]/15 text-[#10b981] border border-[#10b981]/30 rounded-md py-2 hover:bg-[#10b981]/25 flex items-center justify-center gap-2 text-sm font-medium"><Check className="w-4 h-4" /> Accept</button>
                    <button data-testid="app-deny" onClick={() => review(active.id, "deny")} className="flex-1 bg-red-500/15 text-red-400 border border-red-500/30 rounded-md py-2 hover:bg-red-500/25 flex items-center justify-center gap-2 text-sm font-medium"><X className="w-4 h-4" /> Deny</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <ApplicationPanelModal open={modalOpen} initial={editing} saving={saving} guildId={guildId} onClose={() => { setModalOpen(false); setEditing(null); }} onSave={savePanel} />
    </div>
  );
}
